import React, { useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  selectLocalStats,
  selectLeaderboard,
  selectIsLoadingLeaderboard,
  setLeaderboard,
  setLoadingLeaderboard,
  PlayerStats,
} from "../store/profileSlice";
import { useFarcaster } from "../contexts/FarcasterContext";
import PlayerAvatar from "../components/multiplayer/PlayerAvatar";
import { SmallButton } from "../components/UI/smallButton/SmallButton";
import { media } from "../styles/GlobalStyles";
import Layout from "../components/UI/layout/Layout";

// The API server URL (Socket.IO + REST endpoints)
const SERVER_API_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:3001");

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContent = styled.div`
  width: 100%;
  max-width: 48rem;
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const BackBtn = styled(SmallButton)`
  align-self: flex-start;
  margin-bottom: 2rem;
`;

const ProfileCard = styled(motion.div)`
  background: var(--color-white);
  border: 3px solid var(--color-black);
  box-shadow: 0 1rem 0 var(--color-black);
  border-radius: 2rem;
  padding: 2.8rem;
  width: 100%;
  max-width: 48rem;
  margin-bottom: 2.4rem;

  ${media.phone} {
    padding: 2rem;
  }
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  margin-bottom: 2rem;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.h2`
  font-size: var(--font-size-heading-m);
  color: var(--color-black);
`;

const UserHandle = styled.p`
  font-size: 1.4rem;
  color: rgba(0, 0, 0, 0.5);
`;

const FidTag = styled.span`
  display: inline-block;
  background: #f3f4f6;
  border-radius: 2rem;
  padding: 0.2rem 0.8rem;
  font-size: 1.2rem;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 0.4rem;
`;

const SectionTitle = styled.h3`
  font-size: var(--font-size-heading-xs);
  text-transform: uppercase;
  color: var(--color-black);
  margin-bottom: 1.2rem;
  letter-spacing: 0.05em;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  ${media.phone} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatBox = styled.div<{ accent: string }>`
  background: ${({ accent }) => accent};
  border-radius: 1.2rem;
  padding: 1.2rem 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2.8rem;
  font-weight: bold;
  color: var(--color-white);
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  margin-top: 0.3rem;
`;

const PointsBar = styled.div`
  background: rgba(0, 0, 0, 0.06);
  border-radius: 0.8rem;
  height: 0.8rem;
  overflow: hidden;
  margin-top: 1.2rem;
`;

const PointsFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${({ pct }) => pct}%;
  background: linear-gradient(90deg, #7945ff, #fd6687);
  border-radius: 0.8rem;
  transition: width 0.5s ease;
`;

const PointsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
`;

const PointsLabel = styled.span`
  font-size: 1.3rem;
  color: rgba(0, 0, 0, 0.6);
`;

const PointsValue = styled.span`
  font-size: 1.6rem;
  font-weight: bold;
  color: var(--color-dark-purple);
`;

// Leaderboard

const LeaderboardCard = styled(ProfileCard)``;

const LeaderRow = styled.div<{ isMe?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.8rem 1rem;
  border-radius: 1rem;
  background: ${({ isMe }) =>
    isMe
      ? "linear-gradient(135deg, rgba(121,69,255,0.12), rgba(253,102,135,0.08))"
      : "transparent"};
  border: ${({ isMe }) =>
    isMe ? "2px solid rgba(121,69,255,0.3)" : "2px solid transparent"};
  margin-bottom: 0.6rem;
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background: ${({ rank }) =>
    rank === 1
      ? "linear-gradient(135deg,#ffd700,#ffb800)"
      : rank === 2
        ? "linear-gradient(135deg,#c0c0c0,#a8a8a8)"
        : rank === 3
          ? "linear-gradient(135deg,#cd7f32,#b06820)"
          : "rgba(0,0,0,0.1)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.3rem;
  color: ${({ rank }) => (rank <= 3 ? "white" : "rgba(0,0,0,0.5)")};
  flex-shrink: 0;
`;

const LeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeaderName = styled.p`
  font-size: 1.4rem;
  font-weight: bold;
  color: var(--color-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LeaderSub = styled.p`
  font-size: 1.1rem;
  color: rgba(0, 0, 0, 0.45);
`;

const LeaderPoints = styled.div`
  font-size: 1.6rem;
  font-weight: bold;
  color: var(--color-dark-purple);
  white-space: nowrap;
`;

const EmptyLeaderboard = styled.p`
  color: rgba(0, 0, 0, 0.4);
  font-size: 1.4rem;
  text-align: center;
  padding: 2rem;
  font-style: italic;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  font-size: 1.4rem;
  color: rgba(0, 0, 0, 0.4);
`;

// ─── Component ────────────────────────────────────────────────────────────────

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useFarcaster();
  const localStats = useAppSelector(selectLocalStats);
  const leaderboard = useAppSelector(selectLeaderboard);
  const isLoading = useAppSelector(selectIsLoadingLeaderboard);

  // Fetch leaderboard
  useEffect(() => {
    dispatch(setLoadingLeaderboard(true));
    fetch(`${SERVER_API_URL}/api/leaderboard`)
      .then((r) => r.json())
      .then((data: PlayerStats[]) => dispatch(setLeaderboard(data)))
      .catch(() => dispatch(setLoadingLeaderboard(false)));
  }, [dispatch]);

  const winRate =
    localStats.gamesPlayed > 0
      ? Math.round((localStats.wins / localStats.gamesPlayed) * 100)
      : 0;

  // Max points if every game was a win (win=10 + participation=1 = 11 pts/game)
  const maxPossiblePoints = Math.max(localStats.gamesPlayed * 11, 1);
  const pointsPct = Math.min(
    100,
    Math.round((localStats.totalPoints / maxPossiblePoints) * 100),
  );

  const myFid = user?.fid;
  const displayName = user?.displayName || user?.username || "Anonymous";

  return (
    <Layout color="darkPurple" isGamePage>
      <PageContent>
        <BackBtn onClick={() => navigate("/")}>← Back to Menu</BackBtn>

        {/* ── My Profile ── */}
        <ProfileCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <UserRow>
            <PlayerAvatar
              pfpUrl={user?.pfpUrl}
              name={displayName}
              size={72}
              ringColor="#7945ff"
            />
            <UserInfo>
              <UserName>{displayName}</UserName>
              {user?.username && <UserHandle>@{user.username}</UserHandle>}
              {user?.fid && <FidTag>FID #{user.fid}</FidTag>}
            </UserInfo>
          </UserRow>

          <SectionTitle>My Stats</SectionTitle>
          <StatsGrid>
            <StatBox accent="linear-gradient(135deg,#22c55e,#16a34a)">
              <StatValue>{localStats.wins}</StatValue>
              <StatLabel>Wins</StatLabel>
            </StatBox>
            <StatBox accent="linear-gradient(135deg,#ef4444,#dc2626)">
              <StatValue>{localStats.losses}</StatValue>
              <StatLabel>Losses</StatLabel>
            </StatBox>
            <StatBox accent="linear-gradient(135deg,#f59e0b,#d97706)">
              <StatValue>{localStats.ties}</StatValue>
              <StatLabel>Draws</StatLabel>
            </StatBox>
            <StatBox accent="linear-gradient(135deg,#7945ff,#5c2dd5)">
              <StatValue>{winRate}%</StatValue>
              <StatLabel>Win Rate</StatLabel>
            </StatBox>
          </StatsGrid>

          <PointsRow>
            <PointsLabel>⭐ Total Points</PointsLabel>
            <PointsValue>{localStats.totalPoints.toLocaleString()}</PointsValue>
          </PointsRow>
          <PointsBar>
            <PointsFill pct={pointsPct} />
          </PointsBar>
          <PointsRow style={{ marginTop: "0.4rem" }}>
            <PointsLabel style={{ fontSize: "1.1rem" }}>
              Win=10pts · Draw=3pts · Play=1pt
            </PointsLabel>
          </PointsRow>
        </ProfileCard>

        {/* ── Global Leaderboard ── */}
        <LeaderboardCard
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <SectionTitle>🏆 Global Leaderboard</SectionTitle>
          {isLoading ? (
            <LoadingSpinner>Loading…</LoadingSpinner>
          ) : leaderboard.length === 0 ? (
            <EmptyLeaderboard>
              No players on the board yet. Be the first!
            </EmptyLeaderboard>
          ) : (
            leaderboard.slice(0, 20).map((player, idx) => {
              const isMe = Boolean(myFid && player.fid === myFid);
              const name =
                player.displayName ||
                player.username ||
                `Player #${player.fid ?? idx + 1}`;
              return (
                <LeaderRow key={player.fid ?? idx} isMe={isMe}>
                  <RankBadge rank={idx + 1}>{idx + 1}</RankBadge>
                  <PlayerAvatar
                    pfpUrl={player.pfpUrl}
                    name={name}
                    size={36}
                    ringColor={isMe ? "#7945ff" : "rgba(0,0,0,0.15)"}
                  />
                  <LeaderInfo>
                    <LeaderName>
                      {player.username ? `@${player.username}` : name}
                      {isMe && " (You)"}
                    </LeaderName>
                    <LeaderSub>
                      {player.wins}W · {player.losses}L · {player.ties}D{" · "}
                      {player.gamesPlayed} games
                    </LeaderSub>
                  </LeaderInfo>
                  <LeaderPoints>
                    {player.totalPoints.toLocaleString()}pts
                  </LeaderPoints>
                </LeaderRow>
              );
            })
          )}
        </LeaderboardCard>
      </PageContent>
    </Layout>
  );
};

export default Profile;
